import React, { useEffect, useRef, useState } from 'react';
import EventSystem from '../../EventSystem';
import { FeatureTour } from '../../components/featureTour/FeatureTour';
import {
    isFeatureTourOpen,
    recordFeatureTourAutoShown,
    recordFeatureTourClosed,
    shouldAutoStartFeatureTour,
} from '../../components/featureTour/featureTourState';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';
import { QUICK_VIEW_TOUR_EVENT } from './QuickViewDrawer';
import { createQuickViewTour, QUICK_VIEW_TOUR_ID, QUICK_VIEW_TOUR_VERSION } from './quickViewTour';

interface QuickViewTourControllerProps {
    isAuthenticated: boolean;
    isQuickViewOpen: boolean;
    // the header button's own toggle: opens Quick View when it is closed and closes it when it is open
    onToggleQuickView: () => void;
    // lets the drawer stop trapping focus while the tour card (which lives outside it) is being used
    onRunningChange: (isRunning: boolean) => void;
}

// Runs the Quick View tour: pops up on its own for a signed-in user who has Quick View (until it is finished or "Don't show
// this again" is ticked), and plays any time from the ? in Quick View's header. Rendered by AppContent, because the tour
// starts with the header button and has to open the panel itself.
export const QuickViewTourController: React.FC<QuickViewTourControllerProps> = ({ isAuthenticated, isQuickViewOpen, onToggleQuickView, onRunningChange }) => {
    const [launch, setLaunch] = useState<'auto' | 'manual' | null>(null);
    // refs, so the steps (created once) always read and call the current ones
    const isQuickViewOpenRef = useRef(isQuickViewOpen);
    isQuickViewOpenRef.current = isQuickViewOpen;
    const onToggleQuickViewRef = useRef(onToggleQuickView);
    onToggleQuickViewRef.current = onToggleQuickView;
    const hasAutoStartedRef = useRef(false);

    const [quickViewTour] = useState(() => createQuickViewTour({
        isQuickViewOpen: () => isQuickViewOpenRef.current,
        openQuickView: () => onToggleQuickViewRef.current(),
        closeQuickView: () => onToggleQuickViewRef.current(),
    }));

    useEffect(() => {
        onRunningChange(launch !== null);
        // only a change of launch matters; onRunningChange is the parent's own setter
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [launch]);

    useEffect(() => {
        const onTourRequested = () => {
            if (!isFeatureTourOpen()) {
                setLaunch('manual');
            }
        };
        EventSystem.on(QUICK_VIEW_TOUR_EVENT, onTourRequested);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_TOUR_EVENT, onTourRequested); };
    }, []);

    // A few seconds after signing in, so it does not land on top of the page still loading.
    useEffect(() => {
        if (!isAuthenticated || hasAutoStartedRef.current
            || !UserFeature.HasAccess(UserFeatureEnum.hasQuickView)
            || !shouldAutoStartFeatureTour(QUICK_VIEW_TOUR_ID, QUICK_VIEW_TOUR_VERSION)) {
            return;
        }
        const autoStartTimer = window.setTimeout(() => {
            // another tour is already up: leave this one for a later visit
            if (isFeatureTourOpen()) {
                return;
            }
            hasAutoStartedRef.current = true;
            recordFeatureTourAutoShown(QUICK_VIEW_TOUR_ID, QUICK_VIEW_TOUR_VERSION);
            setLaunch('auto');
        }, 3000);
        return () => window.clearTimeout(autoStartTimer);
    }, [isAuthenticated]);

    const onClose = (result: { isCompleted: boolean; isDontShowAgain: boolean }) => {
        quickViewTour.restorePage();
        recordFeatureTourClosed(QUICK_VIEW_TOUR_ID, QUICK_VIEW_TOUR_VERSION, result.isCompleted || result.isDontShowAgain);
        setLaunch(null);
    };

    if (!launch) {
        return null;
    }
    return <FeatureTour steps={quickViewTour.steps} showDontShowAgain={launch === 'auto'} onClose={onClose} />;
};
